"use client";

import { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import type { ReviewerFeedback, ReviewerSeverity } from '@/types/reviewer';

interface ReviewerCalloutProps {
  feedback: ReviewerFeedback;
  onRequestChanges?: () => void;
  onViewDetails?: () => void;
}

const severityConfig: Record<ReviewerSeverity, {
  icon: typeof InformationCircleIcon;
  bg: string;
  border: string;
  text: string;
  badge: string;
}> = {
  info: {
    icon: InformationCircleIcon,
    bg: 'bg-info/10',
    border: 'border-info/30',
    text: 'text-info',
    badge: 'bg-info/10 text-info',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    text: 'text-warning',
    badge: 'bg-warning/10 text-warning',
  },
  critical: {
    icon: ExclamationCircleIcon,
    bg: 'bg-destructive/10',
    border: 'border-destructive/30',
    text: 'text-destructive',
    badge: 'bg-destructive/10 text-destructive',
  },
};

export function ReviewerCallout({ feedback, onRequestChanges, onViewDetails }: ReviewerCalloutProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const severity = feedback.severity || 'info';
  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <div
      className={`mt-3 rounded-lg border ${config.border} ${config.bg} p-4 transition-all duration-200`}
      role="region"
      aria-label="Reviewer feedback"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Icon className={`w-5 h-5 ${config.text} flex-shrink-0 mt-1`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-medium px-2 py-1 rounded ${config.badge}`}>
                Reviewer Pass
              </span>
            </div>
            <p className={`text-sm ${config.text} font-medium`}>
              {feedback.summary}
            </p>
          </div>
        </div>

        {feedback.bullets.length > 0 && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className={config.text}
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </Button>
        )}
      </div>

      {isExpanded && feedback.bullets.length > 0 && (
        <div className="mt-3 pl-8">
          <ul className={`space-y-2 list-disc ml-4 text-sm ${config.text} opacity-90`}>
            {feedback.bullets.map((bullet, idx) => (
              <li key={`${feedback.id}-bullet-${idx}`}>
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(onRequestChanges || onViewDetails) && (
        <div className="mt-3 flex items-center gap-2 pl-8">
          {feedback.bullets.length > 0 && onViewDetails && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewDetails}
              className="text-xs font-medium text-primary hover:underline"
            >
              View Full Details
            </Button>
          )}
          {onRequestChanges && (
            <Button
              variant="glow"
              size="sm"
              onClick={onRequestChanges}
              className="text-xs font-medium"
            >
              Request Changes
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
